// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CostTimeSeriesPoint,
  TimeRecordTimeSeriesPoint,
} from '@/costs/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import { localPoint } from '@visx/event';
import { MouseEvent } from 'react';

export interface ChartData {
  label: string;
  fullLabel: string;
  value: number;
  rawData: CostTimeSeriesPoint | TimeRecordTimeSeriesPoint;
}

export interface TooltipData {
  label: string;
  value: number;
  formattedValue: string;
  count: number;
}

interface ShowTooltipParams {
  tooltipData: TooltipData;
  tooltipLeft: number;
  tooltipTop: number;
}

type ShowTooltipFn = (params: ShowTooltipParams) => void;

/**
 * Helper to create standardized data-datum attribute value
 */
export const createDataDatumAttribute = (datum: ChartData): string =>
  JSON.stringify(datum);

/**
 * Formats minutes to fractional hours for analytics display
 * Shows hours with maximum precision of 0.1 (e.g., 1.5h, 2.25h, 0.1h)
 */
export const formatMinutesToFractionalHours = (minutes: number): string => {
  if (minutes === 0) return '0h';
  const hours = minutes / 60;

  // If less than 6 minutes (0.1h), show as 0.1h minimum
  if (minutes < 6) return '0.1h';

  // If whole hours, show without decimals
  if (minutes % 60 === 0) return `${Math.round(hours).toString()}h`;

  // Show with 1-2 decimal places as needed, but remove trailing zeros
  const formatted = parseFloat(hours.toFixed(2));
  return `${formatted.toString()}h`;
};

/** Formats a tooltip value as the full currency amount or fractional hours. */
const toFormattedTooltipValue = (
  datum: ChartData,
  mode: 'currency' | 'time',
  currencyCode?: IsoCurrencyCode
): string => {
  if (mode === 'time') {
    return formatMinutesToFractionalHours(datum.value);
  }

  // Currency charts only hold data once a currency is active.
  if (currencyCode === undefined) {
    return formatCurrencyValue(null);
  }

  return formatAmountWithCurrency(
    new CurrencyDecimal(datum.value),
    currencyCode
  );
};

/**
 * Flexible tooltip data formatter that can handle both currency and time modes
 */
const createFlexibleTooltipData = (
  datum: ChartData,
  mode: 'currency' | 'time',
  currencyCode?: IsoCurrencyCode
): TooltipData => ({
  label: datum.fullLabel,
  value: datum.value,
  formattedValue: toFormattedTooltipValue(datum, mode, currencyCode),
  count: datum.rawData.totalEntries,
});

/**
 * Creates a flexible mouse move handler that can handle both currency and time modes
 */
export const createFlexibleTooltipMouseMoveHandler =
  (
    showTooltip: ShowTooltipFn,
    mode: 'currency' | 'time',
    currencyCode?: IsoCurrencyCode
  ) =>
  (event: MouseEvent<SVGElement>) => {
    const point = localPoint(event);
    if (!point) return;

    const datumString = event.currentTarget.getAttribute('data-datum');
    if (!datumString) return;

    const datum = JSON.parse(datumString) as ChartData;

    showTooltip({
      tooltipData: createFlexibleTooltipData(datum, mode, currencyCode),
      tooltipLeft: point.x,
      tooltipTop: point.y,
    });
  };

/**
 * Creates a flexible direct tooltip mouse move handler
 */
export const createDirectFlexibleTooltipMouseMoveHandler =
  (
    showTooltip: ShowTooltipFn,
    datum: ChartData,
    mode: 'currency' | 'time',
    currencyCode?: IsoCurrencyCode
  ) =>
  (event: MouseEvent<SVGElement>) => {
    const point = localPoint(event);
    if (!point) return;

    showTooltip({
      tooltipData: createFlexibleTooltipData(datum, mode, currencyCode),
      tooltipLeft: point.x,
      tooltipTop: point.y,
    });
  };
